// The Map (spec §9): every place card as a pin, every route as coloured segments, place search,
// dropping pins, drawing routes by clicking, importing GPX files, and "along this route" for the
// selected route. Leaflet only draws: the project document stays the one source of truth, so the map
// redraws whenever anyone (or an agent) changes a place or a route.
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { boardsMap, cardsMap, routesMap } from '../../shared/doc.js';
import { MAP_LIMITS, formatDistance, routeColourFor, routeSegments, wrapLon, type LonLat } from '../../shared/geo.js';
import { PIN_COLOURS, listPlaces, listRoutes, placeStatuses, tagColour, type PinStatus, type Place } from '../../shared/map.js';
import { addCard, addDrawnRoute } from '../../shared/ops.js';
import { api, uploadGpx, type MapConfig, type PlaceResult } from '../api.js';
import type { Toast } from '../cards/create.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';
import { PlaceSearch } from './PlaceSearch.js';
import { RoutePanel } from './RoutePanel.js';

type Mode = 'browse' | 'pin' | 'draw';
const START: L.LatLngExpression = [38.6, 23.6]; // Evia, until there's something to fit
const round6 = (x: number) => Math.round(x * 1e6) / 1e6;
const STATUS_LABEL: Record<PinStatus, string> = { question: 'Question', researched: 'Researched', confirmed: 'Confirmed', none: 'No findings' };

/** Text, never HTML: Leaflet renders a string tooltip as HTML, and place names come from people and agents. */
function textNode(text: string): HTMLElement {
  const span = document.createElement('span');
  span.textContent = text;
  return span;
}

export function MapView({ conn, projectId, onOpenCard, toast, readOnly = false, focusPlaceId = null }: {
  conn: ProjectConn;
  projectId: string;
  onOpenCard: (cardId: string) => void;
  toast: Toast;
  readOnly?: boolean;
  focusPlaceId?: string | null;
}) {
  const cardsVersion = useYVersion(cardsMap(conn.doc));
  const boardsVersion = useYVersion(boardsMap(conn.doc)); // accepting a finding changes a pin's status
  const routesVersion = useYVersion(routesMap(conn.doc));
  const [config, setConfig] = useState<MapConfig | null>(null);
  const [colourBy, setColourBy] = useState<'status' | 'tag'>('status');
  const [mode, setMode] = useState<Mode>('browse');
  // Bumped each time the Leaflet map itself is (re)created, so the layer-drawing effects below
  // (which only read refs, not reactive state) know to redraw into the fresh layer groups.
  const [mapGen, setMapGen] = useState(0);
  const [draft, setDraft] = useState<LonLat[]>([]);
  const [selectedRoute, setSelectedRoute] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [locating, setLocating] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layers = useRef<{ routes: L.LayerGroup; pins: L.LayerGroup; draft: L.LayerGroup; me: L.LayerGroup } | null>(null);
  const fitted = useRef(false);
  const focused = useRef<string | null>(null);
  const zoomedTo = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  // Leaflet handlers are bound once; they read the latest values through refs.
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const openRef = useRef(onOpenCard);
  openRef.current = onOpenCard;

  const places = useMemo(() => listPlaces(conn.doc), [conn.doc, cardsVersion]);
  const statuses = useMemo(() => placeStatuses(conn.doc), [conn.doc, cardsVersion, boardsVersion]);
  const routes = useMemo(() => listRoutes(conn.doc), [conn.doc, routesVersion]);
  const tags = useMemo(() => [...new Set(places.map((p) => p.tags[0]).filter((t): t is string => !!t))].sort(), [places]);
  const pinColour = (p: Place) => (colourBy === 'status' ? PIN_COLOURS[statuses.get(p.id) ?? 'none'] : tagColour(p.tags[0]));
  const selected = routes.find((r) => r.id === selectedRoute);
  // D5 (controller ruling): drawing and GPX import are refused client-side past the project's
  // 100-route cap, with a short note, rather than letting people hit the server's 413/400 after
  // drawing or picking a file.
  const atRouteLimit = routes.length >= MAP_LIMITS.routesPerProject;

  useEffect(() => {
    api<MapConfig>('GET', '/api/map-config').then(setConfig, (e: Error) => toast(e.message));
  }, [toast]);

  // The Leaflet map itself: made once the tile settings are known, removed on unmount.
  //
  // Deps are the stable per-connection pieces (conn.doc/undo/me), not `conn` itself: useProject
  // builds a new conn *object* on every status change (connecting/connected/disconnected), even
  // though doc/undo/me never actually change value for the life of this connection. Depending on
  // `conn` meant a network blip or waking from sleep destroyed and recreated the whole map — and
  // the pins/routes/draft/fit effects below, keyed on data that hadn't changed, never redrew into
  // the fresh (empty) layer groups, leaving the map blank. `mapGen` (bumped below) is the signal
  // those effects actually watch for "the map was (re)created, redraw everything".
  useEffect(() => {
    if (!config || !container.current) return;
    const map = L.map(container.current, { worldCopyJump: true }).setView(START, 8);
    // OSM's tile policy wants a Referer; the app's own Referrer-Policy (same-origin) would send none.
    L.tileLayer(config.tileUrl, { attribution: config.attribution, maxZoom: 19, referrerPolicy: 'strict-origin-when-cross-origin' }).addTo(map);
    layers.current = { routes: L.layerGroup().addTo(map), pins: L.layerGroup().addTo(map), draft: L.layerGroup().addTo(map), me: L.layerGroup().addTo(map) };
    map.on('click', (e: L.LeafletMouseEvent) => {
      const p: LonLat = [round6(wrapLon(e.latlng.lng)), round6(e.latlng.lat)];
      if (modeRef.current === 'draw') setDraft((d) => (d.length >= MAP_LIMITS.waypoints ? d : [...d, p]));
      else if (modeRef.current === 'pin') {
        conn.undo.stopCapturing();
        const id = addCard(conn.doc, conn.me, { kind: 'place', name: 'New place', lat: p[1], lon: p[0] }, { map: true });
        setMode('browse');
        openRef.current(id);
      }
    });
    // The map's box changes with the side panel and the window: keep Leaflet's idea of its size right.
    const resize = new ResizeObserver(() => map.invalidateSize());
    resize.observe(container.current);
    mapRef.current = map;
    fitted.current = false;
    focused.current = null;
    zoomedTo.current = null;
    setMapGen((g) => g + 1);
    return () => {
      resize.disconnect();
      map.remove();
      mapRef.current = null;
      layers.current = null;
    };
  }, [config, conn.doc, conn.undo, conn.me]);

  // First view: the focused place (from "Show on map"), otherwise everything there is.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const focus = focusPlaceId ? places.find((p) => p.id === focusPlaceId) : undefined;
    if (focus && focused.current !== focus.id) {
      focused.current = focus.id;
      fitted.current = true;
      map.setView([focus.lat, focus.lon], 14);
      return;
    }
    if (fitted.current) return;
    const points: L.LatLngTuple[] = [...places.map((p): L.LatLngTuple => [p.lat, p.lon]), ...routes.flatMap((r) => routeSegments(r).flatMap((s) => s.latLngs))];
    if (!points.length) return;
    fitted.current = true;
    map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 14 });
  }, [config, places, routes, focusPlaceId, mapGen]);

  // Pins.
  useEffect(() => {
    const l = layers.current;
    if (!l) return;
    l.pins.clearLayers();
    for (const p of places) {
      const marker = L.circleMarker([p.lat, p.lon], { radius: 8, color: '#ffffff', weight: 2, fillColor: pinColour(p), fillOpacity: 1, className: `plana-pin pin-${p.id}` });
      marker.bindTooltip(textNode(p.name || 'Untitled place'));
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        if (modeRef.current === 'browse') openRef.current(p.id);
      });
      marker.addTo(l.pins);
    }
  }, [config, places, statuses, colourBy, mapGen]);

  // Routes, each drawn as its list of segments (spec §13).
  useEffect(() => {
    const l = layers.current;
    if (!l) return;
    l.routes.clearLayers();
    for (const r of routes) {
      for (const seg of routeSegments(r)) {
        const line = L.polyline(seg.latLngs, { color: seg.colour, weight: r.id === selectedRoute ? 7 : 4, opacity: 0.85, className: `plana-route route-${r.id}` });
        line.bindTooltip(textNode(`${r.name || 'Untitled route'} · ${formatDistance(r.lengthM)}`), { sticky: true });
        line.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          if (modeRef.current === 'browse') setSelectedRoute(r.id);
        });
        line.addTo(l.routes);
      }
    }
  }, [config, routes, selectedRoute, mapGen]);

  // The route being drawn.
  useEffect(() => {
    const l = layers.current;
    if (!l) return;
    l.draft.clearLayers();
    if (draft.length > 1) L.polyline(draft.map(([lon, lat]): L.LatLngTuple => [lat, lon]), { color: '#111827', weight: 3, dashArray: '6 6', interactive: false }).addTo(l.draft);
    for (const [lon, lat] of draft) L.circleMarker([lat, lon], { radius: 4, color: '#111827', weight: 2, fillColor: '#ffffff', fillOpacity: 1, interactive: false }).addTo(l.draft);
  }, [config, draft, mapGen]);

  // Disabled while drawing: a quick double-click to place two close points shouldn't also zoom the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (mode === 'draw') map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
  }, [mode, mapGen]);

  // Zoom to a route when it's chosen (from the list, or right after an import).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selected || zoomedTo.current === selected.id) return;
    zoomedTo.current = selected.id;
    fitted.current = true;
    map.fitBounds(L.latLngBounds(routeSegments(selected).flatMap((s) => s.latLngs)), { padding: [30, 30] });
  }, [config, selected, mapGen]);

  // ⌘Z / ⇧⌘Z undo this person's own last change here too (a deleted route, a dropped pin), as on the
  // canvas — except while actively drawing a route: the draft isn't document history yet, so ⌘Z
  // there removes the last clicked point (like "Undo point"), not some unrelated earlier edit.
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      const typing = t instanceof HTMLElement && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));
      if (typing || document.querySelector('.card-panel') || !(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      e.preventDefault();
      if (!e.shiftKey && modeRef.current === 'draw') {
        setDraft((d) => d.slice(0, -1));
        return;
      }
      if (e.shiftKey) conn.undo.redo();
      else conn.undo.undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [conn.undo, readOnly]);

  const addFromSearch = (r: PlaceResult) => {
    conn.undo.stopCapturing();
    const id = addCard(conn.doc, conn.me, { kind: 'place', name: r.name || r.displayName.split(',')[0], lat: r.lat, lon: r.lon, address: r.displayName }, { map: true });
    mapRef.current?.setView([r.lat, r.lon], 14);
    onOpenCard(id);
  };

  const finishDrawing = () => {
    conn.undo.stopCapturing(); // each route is its own undo step
    const id = addDrawnRoute(conn.doc, conn.me, `Route ${routes.length + 1}`, routeColourFor(routes.length), draft);
    if (!id) {
      // Every point landed on the same spot, or outside valid longitude/latitude after wrapping:
      // nothing was written. Keep the draft so they can see what's there and add to it.
      toast('A route needs at least two different points.');
      return;
    }
    setDraft([]);
    setMode('browse');
    setSelectedRoute(id);
  };

  const importFiles = async (files: File[]) => {
    for (const file of files) {
      if (file.size > MAP_LIMITS.gpxBytes) {
        toast(`${file.name}: GPX files can be at most 10 MiB.`);
        continue;
      }
      setImporting(true);
      try {
        const r = await uploadGpx(projectId, file);
        toast(`${file.name}: imported ${r.routes.map((x) => x.name).join(', ')}.`);
        for (const note of r.skipped) toast(`${file.name}: ${note}`);
        if (r.routes[0]) setSelectedRoute(r.routes[0].id);
      } catch (err) {
        toast(`${file.name}: ${(err as Error).message}`);
      } finally {
        setImporting(false);
      }
    }
  };

  const locate = () => {
    const map = mapRef.current;
    if (!map) return;
    setLocating(true);
    // Only one of these ever fires per locate() call: whichever does, removes the other so it
    // doesn't linger as a stale listener (firing from some unrelated later locate(), or pairing
    // with a future call to produce a duplicate toast).
    const onFound = (e: L.LocationEvent) => {
      map.off('locationerror', onError);
      setLocating(false);
      const me = layers.current?.me;
      if (!me) return;
      me.clearLayers();
      L.circle(e.latlng, { radius: e.accuracy, color: '#2563eb', weight: 1, fillOpacity: 0.1, interactive: false }).addTo(me);
      L.circleMarker(e.latlng, { radius: 7, color: '#ffffff', weight: 2, fillColor: '#2563eb', fillOpacity: 1, className: 'plana-me', interactive: false }).addTo(me);
      map.setView(e.latlng, Math.max(map.getZoom(), 13));
    };
    const onError = () => {
      map.off('locationfound', onFound);
      setLocating(false);
      toast("Your location isn't available (permission refused, or no signal).");
    };
    map.once('locationfound', onFound);
    map.once('locationerror', onError);
    map.locate({ enableHighAccuracy: true, timeout: 10_000 });
  };

  return (
    <div className={`map-screen${readOnly ? ' read-only' : ''}`}>
      <aside className="map-side" aria-label="Map tools">
        {!readOnly && (
          <>
            <PlaceSearch projectId={projectId} onPick={addFromSearch} />
            <div className="map-actions">
              <button
                aria-pressed={mode === 'pin'}
                onClick={() => {
                  setDraft([]); // abandons any route in progress, same as switching away from "Draw a route"
                  setMode(mode === 'pin' ? 'browse' : 'pin');
                }}
              >
                {mode === 'pin' ? 'Click the map…' : 'Drop a pin'}
              </button>
              <button
                aria-pressed={mode === 'draw'}
                disabled={mode !== 'draw' && atRouteLimit}
                onClick={() => {
                  setDraft([]);
                  setMode(mode === 'draw' ? 'browse' : 'draw');
                }}
              >
                {mode === 'draw' ? 'Cancel drawing' : 'Draw a route'}
              </button>
              <button disabled={importing || atRouteLimit} onClick={() => fileInput.current?.click()}>
                {importing ? 'Importing…' : 'Import GPX…'}
              </button>
              <input
                ref={fileInput}
                type="file"
                accept=".gpx,application/gpx+xml,application/xml,text/xml"
                multiple
                hidden
                data-testid="gpx-input"
                onChange={(e) => {
                  void importFiles([...(e.target.files ?? [])]);
                  e.target.value = '';
                }}
              />
            </div>
            {atRouteLimit && <p className="muted small">This project has reached its limit of {MAP_LIMITS.routesPerProject} routes: drawing and GPX import are turned off.</p>}
            {mode === 'draw' && (
              <div className="draw-bar" role="status">
                <span>
                  {draft.length} point{draft.length === 1 ? '' : 's'}: click the map to add more.
                </span>
                <button disabled={!draft.length} onClick={() => setDraft((d) => d.slice(0, -1))}>Undo point</button>
                <button disabled={draft.length < 2 || atRouteLimit} onClick={finishDrawing}>Finish route</button>
              </div>
            )}
          </>
        )}
        <button onClick={locate} disabled={locating || !config}>{locating ? 'Finding you…' : 'Show my location'}</button>
        <fieldset className="colour-by">
          <legend>Colour pins by</legend>
          <label>
            <input type="radio" name="colour-by" checked={colourBy === 'status'} onChange={() => setColourBy('status')} /> Findings
          </label>
          <label>
            <input type="radio" name="colour-by" checked={colourBy === 'tag'} onChange={() => setColourBy('tag')} /> Tag
          </label>
        </fieldset>
        <ul className="legend" aria-label="Legend">
          {colourBy === 'status'
            ? (Object.keys(STATUS_LABEL) as PinStatus[]).map((s) => (
                <li key={s}>
                  <span className="dot" style={{ background: PIN_COLOURS[s] }} />
                  {STATUS_LABEL[s]}
                </li>
              ))
            : [...tags, undefined].map((t) => (
                <li key={t ?? '(none)'}>
                  <span className="dot" style={{ background: tagColour(t) }} />
                  {t ?? 'No tag'}
                </li>
              ))}
        </ul>
        <h2>Places ({places.length})</h2>
        <ul className="place-list">
          {places.map((p) => (
            <li key={p.id}>
              <button
                className="link"
                onClick={() => {
                  const map = mapRef.current;
                  map?.setView([p.lat, p.lon], Math.max(map.getZoom(), 13));
                  onOpenCard(p.id);
                }}
              >
                <span className="dot" style={{ background: pinColour(p) }} />
                {p.name || 'Untitled place'}
              </button>
            </li>
          ))}
          {!places.length && <li className="muted small">{readOnly ? 'No places yet.' : 'No places yet. Search above, or drop a pin.'}</li>}
        </ul>
        <h2>Routes ({routes.length})</h2>
        <ul className="route-list">
          {routes.map((r) => (
            <li key={r.id}>
              <button className="link" aria-pressed={r.id === selectedRoute} onClick={() => setSelectedRoute(r.id)}>
                <span className="swatch-line" style={{ background: r.colour }} />
                {r.name || 'Untitled route'} <small className="muted">{formatDistance(r.lengthM)}</small>
              </button>
            </li>
          ))}
          {!routes.length && <li className="muted small">{readOnly ? 'No routes yet.' : 'No routes yet. Draw one, or import a GPX file.'}</li>}
        </ul>
        {selected && (
          // Keyed on the route: without it, switching the selected route reuses the same
          // component instance, so an armed "Delete this route?" and the previous route's
          // along-route list would otherwise carry over onto the newly selected route.
          <RoutePanel key={selected.id} conn={conn} projectId={projectId} route={selected} readOnly={readOnly} onOpenCard={onOpenCard} onClose={() => setSelectedRoute(null)} />
        )}
      </aside>
      <div ref={container} className={`map mode-${mode}`} data-testid="map" />
    </div>
  );
}
